import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, Film, RotateCcw, X } from "lucide-react";
import { useApp } from "../context/AppContext";
import { subscribeUploads, setUploadQueueLabels, retryUpload, dismissUpload } from "../lib/uploadQueue";

export function useUploads() {
  const [jobs, setJobs] = useState([]);
  useEffect(() => subscribeUploads(setJobs), []);
  return jobs;
}

/**
 * Slim progress strip for background video uploads.
 * - conversationId given (chat header): only that chat's uploads.
 * - no conversationId (Shell, global): uploads whose chat is NOT the one on screen.
 */
export function UploadProgressBar({ conversationId = null }) {
  const { t } = useApp();
  const location = useLocation();
  const jobs = useUploads();
  useEffect(() => {
    setUploadQueueLabels({
      done: (name, kind) => t(kind === "photo" ? "photo_sent_to" : "video_sent_to").replace("{name}", name),
      fail: (name, kind) => t(kind === "photo" ? "photo_send_failed_to" : "video_send_failed_to").replace("{name}", name),
      retry: t("retry"),
    });
  }, [t]);
  const openChatId = location.pathname.startsWith("/chat/") ? location.pathname.split("/")[2] : null;
  const mine = jobs.filter((j) => j.status !== "done" && (conversationId ? j.conversationId === conversationId : j.conversationId !== openChatId));
  const shown = mine.filter((j) => j.status !== "error");
  const failed = mine.filter((j) => j.status === "error");
  if (!shown.length && !failed.length) return null;
  if (!shown.length) {
    const f = failed[0];
    return (
      <button type="button" onClick={() => retryUpload(f.id)} className="relative w-full px-3 py-1.5 bg-destructive/10 border-b text-xs flex items-center gap-2 text-left" data-testid="upload-failed-bar">
        <RotateCcw className="h-3.5 w-3.5 text-destructive shrink-0" />
        <span className="truncate text-destructive font-medium">{t(f.kind === "photo" ? "photo_send_failed_to" : "video_send_failed_to").replace("{name}", f.conversationName || "")} — {t("tap_to_retry")}</span>
        <span role="button" aria-label="dismiss" onClick={(e) => { e.stopPropagation(); dismissUpload(f.id); }} className="ml-auto h-6 w-6 rounded-full hover:bg-black/5 flex items-center justify-center shrink-0" data-testid="upload-failed-dismiss"><X className="h-3.5 w-3.5" /></span>
      </button>
    );
  }
  const j = shown[0];
  const pct = Math.round((j.progress || 0) * 100);
  const label = j.status === "compressing" ? t("compressing_video") : j.status === "queued" ? t("queued") : t(j.kind === "photo" ? "sending_photo" : "sending_video");
  return (
    <div className="relative px-3 py-1.5 bg-primary/10 border-b text-xs flex items-center gap-2" data-testid="upload-progress-bar">
      {j.status === "queued" ? <Film className="h-3.5 w-3.5 text-primary shrink-0" /> : <Loader2 className="h-3.5 w-3.5 animate-spin text-primary shrink-0" />}
      <span className="truncate">
        {label}{!conversationId && j.conversationName ? ` → ${j.conversationName}` : ""}{shown.length > 1 ? ` (+${shown.length - 1})` : ""}
      </span>
      <span className="ml-auto tabular-nums font-semibold text-primary" data-testid="upload-progress-pct">{pct}%</span>
      <div className="absolute left-0 right-0 bottom-0 h-0.5 bg-primary/20">
        <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
